/**
 * Ads Collection Slide
 *
 * Slide 3: Shows the collected ads from Meta Ad Library with thumbnails.
 * Prioritizes video thumbnails and shows a mix of videos and images.
 */

import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { Images, Video, Image as ImageIcon, Sparkles, Play } from 'lucide-react'
import type { OnboardingArtifacts } from '../../../contexts/OnboardingContext'

interface AdThumbnail {
  url: string
  isVideo: boolean
}

interface AdsCollectionSlideProps {
  artifacts: OnboardingArtifacts
}

export default function AdsCollectionSlide({ artifacts }: AdsCollectionSlideProps) {
  // Extract ads collection data from artifacts
  // API returns: ads_collected, video_ads, image_ads, ads (array)
  const adsCollection = artifacts.adsCollection as {
    ads_collected?: number
    video_ads?: number
    image_ads?: number
    ads?: Array<{
      media_urls?: string[]
      thumbnail_url?: string
      media_type?: string
    }>
  } | null

  const totalAds = adsCollection?.ads_collected || 0
  const videoCount = adsCollection?.video_ads || 0
  const imageCount = adsCollection?.image_ads || 0

  // Get thumbnails from ads - separate videos and images for better mix
  const allAdsWithUrls = adsCollection?.ads
    ?.filter(ad => ad.media_urls?.[0])
    .map(ad => ({
      url: ad.media_urls![0],
      isVideo: ad.media_type === 'video'
    }))
    .filter(ad => ad.url.startsWith('http')) || []

  // Prioritize mix: take videos first, then fill with images
  const videoAds = allAdsWithUrls.filter(ad => ad.isVideo).slice(0, 4)
  const imageAds = allAdsWithUrls.filter(ad => !ad.isVideo).slice(0, 8 - videoAds.length)
  const mixedAds = [...videoAds, ...imageAds].slice(0, 8)

  // Track which images loaded successfully
  const [validThumbnails, setValidThumbnails] = useState<AdThumbnail[]>([])
  const [loadingImages, setLoadingImages] = useState(true)

  // Preload and validate images
  useEffect(() => {
    if (mixedAds.length === 0) {
      setLoadingImages(false)
      return
    }

    const validated: AdThumbnail[] = []
    let completed = 0

    mixedAds.forEach(ad => {
      const img = new window.Image()
      img.onload = () => {
        validated.push(ad)
        completed++
        if (completed === mixedAds.length) {
          setValidThumbnails(validated.slice(0, 8))
          setLoadingImages(false)
        }
      }
      img.onerror = () => {
        completed++
        if (completed === mixedAds.length) {
          setValidThumbnails(validated.slice(0, 8))
          setLoadingImages(false)
        }
      }
      img.src = ad.url
    })
  }, [mixedAds.map(a => a.url).join(',')])

  // Animated counter
  const [displayCount, setDisplayCount] = useState(0)

  useEffect(() => {
    const duration = 1500
    const steps = 40
    const increment = totalAds / steps
    let current = 0

    const timer = setInterval(() => {
      current += increment
      if (current >= totalAds) {
        setDisplayCount(totalAds)
        clearInterval(timer)
      } else {
        setDisplayCount(Math.floor(current))
      }
    }, duration / steps)

    return () => clearInterval(timer)
  }, [totalAds])

  return (
    <div className="flex flex-col items-center text-center">
      {/* Icon */}
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 0.2, type: 'spring', duration: 0.8 }}
        className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mb-8 shadow-lg"
      >
        <Images className="w-10 h-10 text-white" />
      </motion.div>

      {/* Title */}
      <motion.h2
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-3xl font-bold text-tf-text mb-2"
      >
        Ads Collected
      </motion.h2>

      {/* Counter */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="my-6"
      >
        <div className="text-6xl font-bold bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
          {displayCount}
        </div>
        <p className="text-tf-text-muted mt-2">Creative Assets Found</p>
      </motion.div>

      {/* Stats row */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="flex items-center gap-6 mb-8"
      >
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-500/10">
          <Video className="w-5 h-5 text-rose-400" />
          <span className="text-lg font-semibold text-rose-400">{videoCount}</span>
          <span className="text-sm text-tf-text-muted">Videos</span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/10">
          <ImageIcon className="w-5 h-5 text-blue-400" />
          <span className="text-lg font-semibold text-blue-400">{imageCount}</span>
          <span className="text-sm text-tf-text-muted">Images</span>
        </div>
      </motion.div>

      {/* Thumbnails grid - only show validated images, with video indicators */}
      {!loadingImages && validThumbnails.length > 0 && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="grid grid-cols-4 gap-2 max-w-sm"
        >
          {validThumbnails.map((thumb, i) => (
            <motion.div
              key={thumb.url}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.7 + i * 0.05 }}
              className="relative aspect-square rounded-lg overflow-hidden bg-tf-bg-muted group"
            >
              <img
                src={thumb.url}
                alt={`Ad ${i + 1}`}
                className="w-full h-full object-cover"
              />
              {/* Video indicator overlay */}
              {thumb.isVideo && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center">
                    <Play className="w-4 h-4 text-gray-800 ml-0.5" fill="currentColor" />
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </motion.div>
      )}
      {loadingImages && mixedAds.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="grid grid-cols-4 gap-2 max-w-sm"
        >
          {[...Array(Math.min(8, mixedAds.length))].map((_, i) => (
            <div
              key={i}
              className="aspect-square rounded-lg bg-tf-bg-muted animate-pulse"
            />
          ))}
        </motion.div>
      )}

      {/* Description */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="mt-8 flex items-center gap-2 text-sm text-tf-text-muted"
      >
        <Sparkles className="w-4 h-4 text-purple-400" />
        <span>We&apos;ll analyze these to understand what works</span>
      </motion.div>
    </div>
  )
}
